import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom, catchError, map, of, timeout } from 'rxjs';

import { environment } from '../../../environments/environment';
import { SKIP_TENANT_AUTH_REFRESH } from '../interceptors/auth-refresh-context';

export type TenantMfaChannel = 'EMAIL' | 'AUTHENTICATOR_APP' | 'SMS';

export interface MfaDeliveryCapabilities {
  smsAvailable: boolean;
  smsDestination: string | null;
}

export interface TenantMfaInitiateResponse {
  sent?: boolean;
  channel?: string | null;
  challengeId?: string | null;
  statusCode?: number;
  message?: string;
}

@Injectable({
  providedIn: 'root',
})
export class TenantAuthService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiGatewayUrl;
  private readonly recentTenantLoginKey = 'apexTenantRecentLoginAt';
  private readonly credentialedRequest = { withCredentials: true as const };

  verifySession() {
    return this.http.get<{
      authenticated?: boolean | null;
      accountType?: string | null;
      roles?: string[] | null;
    }>(`${this.base}/api/v1/user/auth-verify`, {
      context: new HttpContext().set(SKIP_TENANT_AUTH_REFRESH, true),
      ...this.credentialedRequest,
    });
  }

  preAuthenticate(payload: {
    email: string;
    password: string;
    rememberMe?: boolean;
    workspaceOrgId?: string | null;
    workspacePortalSlug?: string | null;
  }) {
    const headers = this.workspaceHeaders(payload.workspaceOrgId, payload.workspacePortalSlug);
    return this.http.post<{
      message?: string | null;
      userId: string;
      email?: string | null;
      mfaEnabled: boolean;
      validated: boolean;
      authenticatorConfigured?: boolean | null;
      preAuthToken?: string | null;
      challengeId?: string | null;
    }>(`${this.base}/api/v1/user/pre-authenticate`, payload, { headers, ...this.credentialedRequest });
  }

  login(payload: {
    email: string;
    password: string;
    rememberMe?: boolean;
    workspaceOrgId?: string | null;
    workspacePortalSlug?: string | null;
  }) {
    const body = new URLSearchParams();
    body.set('username', payload.email);
    body.set('password', payload.password);
    body.set('grant_type', 'password');
    body.set('rememberMe', String(payload.rememberMe === true));
    if (payload.workspaceOrgId) {
      body.set('workspaceOrgId', payload.workspaceOrgId);
    }
    if (payload.workspacePortalSlug) {
      body.set('workspacePortalSlug', payload.workspacePortalSlug);
    }

    return this.http.post(`${this.base}/api/v1/user/login`, body.toString(), {
      headers: this.workspaceHeaders(payload.workspaceOrgId, payload.workspacePortalSlug)
        .set('Content-Type', 'application/x-www-form-urlencoded'),
      ...this.credentialedRequest,
    });
  }

  getMfaDeliveryCapabilities(payload: { userId: string; preAuthToken: string; purpose: 'LOGIN' }) {
    return this.http.post<MfaDeliveryCapabilities>(
      `${this.base}/api/v1/user/mfa/delivery-capabilities`, payload, {
        withCredentials: true,
        context: new HttpContext().set(SKIP_TENANT_AUTH_REFRESH, true),
      },
    ).pipe(
      timeout(8000),
      map((response) => ({
        smsAvailable: response?.smsAvailable === true,
        smsDestination: response?.smsAvailable === true && typeof response.smsDestination === 'string'
          ? response.smsDestination : null,
      })),
      catchError(() => of({ smsAvailable: false, smsDestination: null })),
    );
  }

  initiateEmailMfa(payload: { challengeId?: string | null; userId?: string | null }) {
    return this.initiateMfa({ ...payload, channel: 'EMAIL' });
  }

  initiateMfa(payload: { preAuthToken?: string | null; channel: TenantMfaChannel; challengeId?: string | null; userId?: string | null }) {
    const body: Record<string, unknown> = {
      purpose: 'LOGIN',
      channel: payload.channel,
    };
    if (payload.challengeId) {
      body['challengeId'] = payload.challengeId;
    }
    if (payload.userId && (!payload.challengeId || payload.channel === 'SMS')) {
      body['userId'] = payload.userId;
    }

    if (payload.preAuthToken) {
      body['preAuthToken'] = payload.preAuthToken;
    }

    return this.http.post<TenantMfaInitiateResponse>(`${this.base}/api/v1/user/send-token`, body, {
      ...this.credentialedRequest,
    }).pipe(
      map((response) => {
        const confirmed = response?.statusCode === 200
          || (response?.statusCode === undefined && response?.sent === true);
        if (!confirmed || response?.sent === false) {
          throw new Error(response?.message || 'We could not send your security code.');
        }
        return { ...response, sent: true, channel: response.channel ?? payload.channel };
      }),
    );
  }

  verifyEmailMfa(payload: {
    otpCode: string;
    challengeId?: string | null;
    userId?: string | null;
    rememberMe?: boolean;
    workspaceOrgId?: string | null;
    workspacePortalSlug?: string | null;
    preAuthToken?: string | null;
  }) {
    return this.verifyMfa({ ...payload, channel: 'EMAIL' });
  }

  verifyMfa(payload: {
    otpCode: string;
    channel: TenantMfaChannel;
    challengeId?: string | null;
    userId?: string | null;
    email?: string | null;
    rememberMe?: boolean;
    workspaceOrgId?: string | null;
    workspacePortalSlug?: string | null;
    preAuthToken?: string | null;
  }) {
    const body: Record<string, unknown> = {
      otpCode: payload.otpCode,
      purpose: 'LOGIN',
      channel: payload.channel,
      rememberMe: payload.rememberMe === true,
    };
    if (payload.challengeId) {
      body['challengeId'] = payload.challengeId;
    }
    if (payload.userId && (!payload.challengeId || payload.channel === 'SMS')) {
      body['userId'] = payload.userId;
    }
    if (payload.email) {
      body['email'] = payload.email;
    }
    if (payload.workspaceOrgId) {
      body['workspaceOrgId'] = payload.workspaceOrgId;
    }
    if (payload.workspacePortalSlug) {
      body['workspacePortalSlug'] = payload.workspacePortalSlug;
    }
    // Proves this code belongs to the password step that started the login.
    if (payload.preAuthToken) {
      body['preAuthToken'] = payload.preAuthToken;
    }

    return this.http.post<{ message?: string | null; statusCode: number }>(`${this.base}/api/v1/user/verify`, body, {
      headers: this.workspaceHeaders(payload.workspaceOrgId, payload.workspacePortalSlug),
      ...this.credentialedRequest,
    });
  }

  async logout(): Promise<void> {
    await firstValueFrom(
      this.http.post(`${this.base}/api/v1/user/logout`, {}, {
        responseType: 'text',
        ...this.credentialedRequest,
      }).pipe(timeout(8000)),
    ).catch(() => undefined);
  }

  loginUrl(returnPath = '/dashboard', statusParams?: Record<string, string | null | undefined>): string {
    const url = new URL(`${environment.tenantAppUrl.replace(/\/+$/, '')}/login`);
    const cached = this.cachedBrandingContext();
    const workspace = this.workspaceParam() || cached.workspace;
    const orgId = this.orgIdParam() || cached.orgId;

    url.searchParams.set('returnUrl', this.safeReturnPath(returnPath));
    if (workspace) {
      url.searchParams.set('workspace', workspace);
    }
    if (orgId) {
      url.searchParams.set('orgId', orgId);
    }
    if (cached.primaryColor) {
      url.searchParams.set('brandPrimary', cached.primaryColor);
    }
    if (cached.accentColor) {
      url.searchParams.set('brandAccent', cached.accentColor);
    }
    this.applyLoginStatusParams(url, statusParams);

    return url.toString();
  }

  passwordResetUrl(returnPath = '/dashboard'): string {
    const url = new URL(`${environment.publicAppUrl.replace(/\/+$/, '')}/forgot-password`);
    const cached = this.cachedBrandingContext();
    const workspace = this.workspaceParam() || cached.workspace;
    const orgId = this.orgIdParam() || cached.orgId;

    url.searchParams.set('returnApp', 'tenant');
    url.searchParams.set('returnUrl', this.safeReturnPath(returnPath));
    if (workspace) {
      url.searchParams.set('workspace', workspace);
    }
    if (orgId) {
      url.searchParams.set('orgId', orgId);
    }

    return url.toString();
  }

  redirectToLogin(returnPath = '/dashboard', statusParams?: Record<string, string | null | undefined>): void {
    window.location.assign(this.loginUrl(returnPath, statusParams));
  }

  markTenantLoginVerified(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.sessionStorage.setItem(this.recentTenantLoginKey, String(Date.now()));
    } catch {
      // Storage is best-effort only.
    }
  }

  hasRecentTenantLoginGrace(maxAgeMs = 10000): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      const timestamp = Number(window.sessionStorage.getItem(this.recentTenantLoginKey) || '0');
      return Number.isFinite(timestamp) && timestamp > 0 && Date.now() - timestamp <= maxAgeMs;
    } catch {
      return false;
    }
  }

  private safeReturnPath(value: string): string {
    if (!value || value.startsWith('http://') || value.startsWith('https://') || value.startsWith('//')) {
      return '/dashboard';
    }
    return value.startsWith('/') ? value : `/${value}`;
  }

  private applyLoginStatusParams(url: URL, statusParams?: Record<string, string | null | undefined>): void {
    for (const key of ['passwordCreated', 'accountSetup', 'reset']) {
      const raw = statusParams?.[key];
      const value = typeof raw === 'string' ? raw.trim() : '';
      if (value === 'success') {
        url.searchParams.set(key, value);
      }
    }
  }

  private workspaceHeaders(orgId?: string | null, portalSlug?: string | null): HttpHeaders {
    let headers = new HttpHeaders();
    if (portalSlug?.trim()) {
      headers = headers.set('X-Workspace-Portal-Slug', portalSlug.trim());
    }
    if (orgId?.trim()) {
      headers = headers.set('X-Workspace-Org-Id', orgId.trim());
    }
    return headers;
  }

  private workspaceParam(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const url = new URL(window.location.href);
    const explicit = url.searchParams.get('workspace') || url.searchParams.get('org');
    if (explicit?.trim()) {
      return explicit.trim().toLowerCase();
    }

    const host = window.location.hostname.toLowerCase();
    const tenantHost = this.hostname(environment.tenantAppUrl);
    if (!tenantHost || host === tenantHost || !host.endsWith(`.${tenantHost}`)) {
      return null;
    }

    return host.slice(0, -(tenantHost.length + 1));
  }

  private orgIdParam(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const value = new URL(window.location.href).searchParams.get('orgId');
    return value?.trim() || null;
  }

  private cachedBrandingContext(): {
    workspace: string | null;
    orgId: string | null;
    primaryColor: string | null;
    accentColor: string | null;
  } {
    const empty = { workspace: null, orgId: null, primaryColor: null, accentColor: null };
    if (typeof window === 'undefined') {
      return empty;
    }

    try {
      const raw = window.localStorage.getItem('apexTenantWorkspaceBranding.v2');
      const parsed = raw ? JSON.parse(raw) : null;
      const workspace = typeof parsed?.lastWorkspace === 'string'
        ? parsed.lastWorkspace.trim().toLowerCase()
        : '';
      const orgId = typeof parsed?.lastOrgId === 'string'
        ? parsed.lastOrgId.trim()
        : '';
      const cached = (workspace ? parsed?.byWorkspace?.[workspace] : null)
        || (orgId ? parsed?.byOrg?.[orgId] : null)
        || null;

      return {
        workspace: workspace || null,
        orgId: orgId || null,
        primaryColor: this.safeHexColor(cached?.primaryColor),
        accentColor: this.safeHexColor(cached?.accentColor),
      };
    } catch {
      return empty;
    }
  }

  private safeHexColor(value: unknown): string | null {
    if (typeof value !== 'string' || !/^#[0-9A-Fa-f]{6}$/.test(value.trim())) {
      return null;
    }

    const color = value.trim();
    const normalized = color.toUpperCase();
    return normalized === '#2FC1F1' || normalized === '#35C0EF' ? null : color;
  }

  private hostname(value: string): string | null {
    try {
      return new URL(value).hostname.toLowerCase();
    } catch {
      return null;
    }
  }
}
